// ============================================================================
// HEADER KEYBOARD - Keyboard mode and shortcuts (lazy-loaded on Tab keypress)
// ============================================================================

// Mark as loaded immediately on execution (prevents duplicate loads)
window.__keyboardLoaded = true;

import { dom, getWidth, undoInert, focusSearch, closeEverything } from './header-core.js';
import { debugLog } from '../utils/debug.js';

const template = window.__CART_CONFIG__?.template;

const isCartOpen = () => document.documentElement.getAttribute('data-cart-open') === 'true';

const getGiftBtn = () => document.querySelector('.giftNoteBtn');

const navElements = {
  navAll: document.querySelector('#sub-header-links [data-text="All"]'),
  navMatcha: document.querySelector('#sub-header-links [data-text="Matcha"]'),
  navSencha: document.querySelector('#sub-header-links [data-text="Sencha"]'),
  navBancha: document.querySelector('#sub-header-links [data-text="Bancha"]'),
  navGyokuro: document.querySelector('#sub-header-links [data-text="Gyokuro"]'),
  navGifts: document.querySelector('#sub-header-links [data-text="Holiday Gifts"]'),
  navTeabags: document.querySelector('#sub-header-links [data-text="Teabags"]'),
  navUtensils: document.querySelector('#sub-header-links [data-text="Utensils"]'),
};

const KEYBOARD_SHORTCUTS = {
  h: { combo: 'z', action: () => (window.location.href = '/') },
  k: {
    combo: 'z',
    action: (e) => {
      e.preventDefault();
      const skipto = document.getElementById('skipto');
      if (!skipto) return;
      // Keep fallback in sync with CSS-driven header heights.
      const fallbackHeaderHeight = document.getElementById('headerFill')?.offsetHeight || 142;
      const headerHeight = document.getElementById('header')?.offsetHeight || fallbackHeaderHeight;
      const targetY = skipto.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
      const focusableSelector =
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const nextFocusable = [...document.querySelectorAll(focusableSelector)].find(
        (el) => skipto.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
      );
      nextFocusable?.focus({ focusVisible: true });
    },
  },
  x: { combo: 'z', action: () => (isCartOpen() ? window.closeCart?.() : window.openCart?.()) },
  q: {
    combo: 'z',
    action: () => document.querySelector('.header-account')?.click(),
  },
  c: { combo: 'z', action: () => startLoad?.() },
  ' ': { combo: 'z', action: () => dom.btnChat?.click() },
  i: {
    combo: 'z',
    action: () =>
      isCartOpen() ? getGiftBtn()?.click() : (window.location.href = '/collections/holiday-gifts'),
  },
  f: {
    combo: 'z',
    action: (e) => {
      focusSearch();
      e.preventDefault();
    },
  },
  // Add to bag (product pages only). Kept off `a` so z + a always opens All.
  d: {
    combo: 'z',
    action: () => {
      if (template === 'product') dom.addButton?.click();
    },
  },
  // Collections
  a: { combo: 'z', action: () => (window.location.href = '/collections/all') },
  m: { combo: 'z', action: () => (window.location.href = '/collections/matcha') },
  s: { combo: 'z', action: () => (window.location.href = '/collections/sencha') },
  b: { combo: 'z', action: () => (window.location.href = '/collections/bancha') },
  g: { combo: 'z', action: () => (window.location.href = '/collections/gyokuro') },
  t: { combo: 'z', action: () => (window.location.href = '/collections/teabags') },
  u: { combo: 'z', action: () => (window.location.href = '/collections/utensils') },
  '.': {
    combo: 'z',
    action: () => {
      const link = document.querySelector('[data-nav-next]');
      if (link?.href && link.getAttribute('aria-disabled') !== 'true') {
        window.location.href = link.href;
      }
    },
  },
  ',': {
    combo: 'z',
    action: () => {
      const link = document.querySelector('[data-nav-prev]');
      if (link?.href && link.getAttribute('aria-disabled') !== 'true') {
        window.location.href = link.href;
      }
    },
  },
};

const isKeyPressed = {};
const executedShortcuts = new Set();
let escapeTimer = null;

function replaceAt(str, index, replacement) {
  return str.substring(0, index) + replacement + str.substring(index + 1);
}

function underlineChar(el, key, index) {
  if (!el) return;
  el.innerHTML = `<span class="keySpan">${replaceAt(el.innerText, index, `<kbd data-key=${key}>${key}</kbd>`)}</span>`;
}

function prependKey(el, key) {
  if (!el) return;
  el.innerHTML = `<span class="keySpan"><kbd data-key=${key}>${key}</kbd> ${el.dataset.text}</span>`;
}

function cleanChar(el) {
  if (!el) return;
  el.innerHTML = `<span class="keySpan">${el.dataset.text}</span>`;
}

function showNavKeys() {
  const prevLink = document.querySelector('[data-nav-prev]');
  const nextLink = document.querySelector('[data-nav-next]');
  if (prevLink && prevLink.getAttribute('aria-disabled') !== 'true') {
    const svg = prevLink.querySelector('svg');
    if (svg) {
      prevLink._savedSvg = svg;
      const kbd = document.createElement('kbd');
      kbd.setAttribute('data-key', ',');
      kbd.textContent = '<';
      svg.replaceWith(kbd);
    }
  }
  if (nextLink && nextLink.getAttribute('aria-disabled') !== 'true') {
    const svg = nextLink.querySelector('svg');
    if (svg) {
      nextLink._savedSvg = svg;
      const kbd = document.createElement('kbd');
      kbd.setAttribute('data-key', '.');
      kbd.textContent = '>';
      svg.replaceWith(kbd);
    }
  }
}

function hideNavKeys() {
  const prevLink = document.querySelector('[data-nav-prev]');
  const nextLink = document.querySelector('[data-nav-next]');
  if (prevLink?._savedSvg) {
    prevLink.querySelector('kbd[data-key=","]')?.replaceWith(prevLink._savedSvg);
    delete prevLink._savedSvg;
  }
  if (nextLink?._savedSvg) {
    nextLink.querySelector('kbd[data-key="."]')?.replaceWith(nextLink._savedSvg);
    delete nextLink._savedSvg;
  }
}

function enterKeyMode() {
  if (getWidth() > 680) {
    undoInert();
    debugLog('Entering keymode');
    document.documentElement.setAttribute('data-keymode', 'true');
    document.documentElement.style.setProperty('--clr-focus', '#eb9031');
    localStorage.setItem('keymode', 'true');
  }
}

function exitKeyMode() {
  debugLog('Exiting keymode mode');
  escapeTimer = null;
  document
    .querySelectorAll('kbd[data-key="esc"]')
    .forEach((el) => el.classList.remove('keyPressed'));
  document.documentElement.style.setProperty('--clr-focus', '#D29B2D50');
  document.documentElement.removeAttribute('data-keymode');
  localStorage.removeItem('keymode');
}

// Expose exitKeyMode for header-core.js scroll handler
window.__exitKeyMode = exitKeyMode;

function setKeyVisual(key, pressed) {
  const method = pressed ? 'add' : 'remove';
  const normalizedKey = key === ' ' ? ' ' : key;
  document
    .querySelectorAll(`kbd[data-key="${normalizedKey}"]`)
    .forEach((el) => el.classList[method]('keyPressed'));
}

function handleKeyUp(e) {
  if (escapeTimer && (e.key === 'Esc' || e.key === 'Escape')) {
    clearTimeout(escapeTimer);
    escapeTimer = null;
  }

  if (localStorage.getItem('keymode') !== 'true') return;

  const key = e.key.toLowerCase();
  setKeyVisual(key === 'escape' ? 'esc' : key, false);

  if (key === 'z') {
    cleanChar(navElements.navAll);
    cleanChar(navElements.navMatcha);
    cleanChar(navElements.navSencha);
    cleanChar(navElements.navBancha);
    cleanChar(navElements.navGyokuro);
    cleanChar(navElements.navGifts);
    cleanChar(navElements.navTeabags);
    cleanChar(navElements.navUtensils);
    cleanChar(getGiftBtn());
    hideNavKeys();
    executedShortcuts.clear();
  } else {
    executedShortcuts.delete(key);
  }

  isKeyPressed[e.key.toLowerCase()] = false;
}

function handleKeyDown(e) {
  const keyMode = localStorage.getItem('keymode');

  if (e.key === 'Esc' || e.key === 'Escape') {
    closeEverything();
    if (!e.repeat && !escapeTimer) {
      escapeTimer = setTimeout(exitKeyMode, 1200);
    }
  }

  if (keyMode === 'true') {
    const key = e.key.toLowerCase();
    setKeyVisual(key === 'escape' ? 'esc' : key, true);

    if (key === 'z' && !e.repeat) {
      underlineChar(navElements.navAll, 'a', 0);
      underlineChar(navElements.navMatcha, 'm', 0);
      underlineChar(navElements.navSencha, 's', 0);
      underlineChar(navElements.navBancha, 'b', 0);
      underlineChar(navElements.navGyokuro, 'g', 0);
      underlineChar(navElements.navGifts, 'i', 3);
      underlineChar(navElements.navTeabags, 't', 0);
      underlineChar(navElements.navUtensils, 'u', 0);
      prependKey(getGiftBtn(), 'i');
      showNavKeys();
    }

    isKeyPressed[e.key.toLowerCase()] = true;

    if (!e.repeat) {
      Object.entries(KEYBOARD_SHORTCUTS).forEach(([shortcutKey, { combo, action }]) => {
        if (
          isKeyPressed[combo] &&
          isKeyPressed[shortcutKey] &&
          !executedShortcuts.has(shortcutKey)
        ) {
          executedShortcuts.add(shortcutKey);
          action(e);
        }
      });
    }
  } else if (e.key === 'Tab') {
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
      enterKeyMode();
    }
  }
}

// Initialize keyboard mode if previously enabled or Tab was pressed while loading
if (localStorage.getItem('keymode') === 'true' || window.__pendingKeyMode) {
  delete window.__pendingKeyMode;
  enterKeyMode();
}

// ============================================================================
// ACTION BUTTON ACCESSIBILITY
// ============================================================================

const isActivationKey = (key) => key === ' ' || key === 'Spacebar' || key === 'Enter';

function actionButtonKeydownHandler(event) {
  if (isActivationKey(event.key)) {
    event.preventDefault();
    this.classList.add('pseudoHover');
  }
}

function actionButtonKeyupHandler(event) {
  if (isActivationKey(event.key)) {
    event.preventDefault();
    this.classList.remove('pseudoHover');
    this.click();
  }
}

function setupActionButtons(selector) {
  document.querySelectorAll(selector).forEach((el) => {
    el.addEventListener('keydown', actionButtonKeydownHandler.bind(el));
    el.addEventListener('keyup', actionButtonKeyupHandler.bind(el));
  });
}

// Setup action buttons for static elements
setupActionButtons('a[role="button"],label[role="button"],span[role="button"],div[role="button"]');
if (template === 'product') {
  setupActionButtons('img[role="button"]');
}

// Setup keyboard event listeners
document.addEventListener('keyup', handleKeyUp);
document.addEventListener('keydown', handleKeyDown);

// Cart keyboard support
if (dom.cartNode) {
  dom.cartNode.addEventListener('focusin', (e) => {
    e.target.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  });

  dom.cartNode.addEventListener('keydown', (e) => {
    const btn = e.target.closest('a[role="button"]');
    if (!btn || !isActivationKey(e.key)) return;
    e.preventDefault();
    btn.classList.add('pseudoHover');
  });

  dom.cartNode.addEventListener('keyup', (e) => {
    const btn = e.target.closest('a[role="button"]');
    if (!btn || !isActivationKey(e.key)) return;
    e.preventDefault();
    btn.classList.remove('pseudoHover');

    const row = btn.closest('[data-key]');
    const itemKey = row?.dataset.key;
    const ariaLabel = btn.getAttribute('aria-label');
    const isQty = btn.classList.contains('item-qty');
    const isRecommended = btn.id === 'addRecommended';

    btn.click();

    if (itemKey && (ariaLabel || isQty)) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newRow = dom.cartNode.querySelector(`[data-key="${itemKey}"]`);
          if (!newRow) return;
          const selector = isQty ? '.item-qty' : `[aria-label="${ariaLabel}"]`;
          newRow.querySelector(selector)?.focus();
        });
      });
    } else if (isRecommended) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.getElementById('addRecommended')?.focus();
        });
      });
    }
  });
}

document.body?.classList.remove('cart-loading');
debugLog('[Header] Keyboard module loaded');
